import { ProgressBar } from '@yoast/ui-library';

import SparklesGradientIcon from '../../assets/icon-sparkles-gradient.svg?react';

const ENOUGH_PERCENT = 35;
const OPTIMAL_PERCENT = 85;

type Props = {
  /** True when brand_name + description are filled. */
  hasEnough: boolean;
  /** True when 6+ fields are filled. */
  hasOptimal: boolean;
  /** True when every tracked field is filled. */
  isComplete: boolean;
  /** Number of filled fields (used to interpolate position between thresholds). */
  filledCount: number;
  /** Total number of fields tracked (for the upper bound). */
  totalCount: number;
};

const computePercent = (
  hasEnough: boolean,
  hasOptimal: boolean,
  filledCount: number,
  totalCount: number,
): number => {
  if (filledCount === 0) return 0;
  if (!hasEnough) {
    return Math.min(ENOUGH_PERCENT - 4, (filledCount / 2) * ENOUGH_PERCENT);
  }
  if (!hasOptimal) {
    const span = OPTIMAL_PERCENT - ENOUGH_PERCENT;
    const progressInBand = Math.min(1, (filledCount - 2) / 4);
    return ENOUGH_PERCENT + span * progressInBand;
  }
  const span = 100 - OPTIMAL_PERCENT;
  const progressInBand = Math.min(1, (filledCount - 6) / Math.max(1, totalCount - 6));
  return OPTIMAL_PERCENT + span * progressInBand;
};

const getPhaseLabel = (
  filledCount: number,
  hasEnough: boolean,
  isComplete: boolean,
): { text: string; colorClass: string } | null => {
  if (filledCount === 0) return null;
  if (isComplete) {
    return { text: 'Complete', colorClass: 'yst-text-emerald-600' };
  }
  if (!hasEnough) {
    return { text: 'Starter', colorClass: 'yst-text-amber-600' };
  }
  return { text: 'Optimal', colorClass: 'yst-text-emerald-600' };
};

const getProgressClass = (hasEnough: boolean, hasOptimal: boolean): string => {
  if (hasOptimal) {
    return 'yst-h-full yst-bg-emerald-500 yst-transition-[width,background-color] yst-duration-500';
  }
  if (hasEnough) {
    return 'yst-h-full yst-bg-emerald-400 yst-bg-gradient-to-r yst-from-emerald-400 yst-to-emerald-500 yst-transition-[width,background-color] yst-duration-500';
  }
  return 'yst-h-full yst-bg-amber-400 yst-bg-gradient-to-r yst-from-amber-300 yst-to-amber-500 yst-transition-[width,background-color] yst-duration-500';
};

const ThresholdProgressBar = ({
  hasEnough,
  hasOptimal,
  isComplete,
  filledCount,
  totalCount,
}: Props) => {
  const percent = computePercent(hasEnough, hasOptimal, filledCount, totalCount);
  const phaseLabel = getPhaseLabel(filledCount, hasEnough, isComplete);
  const tickColorClass = hasEnough ? 'yst-bg-emerald-500' : 'yst-bg-slate-400';

  return (
    <div className="yst-relative yst-pt-6 yst-pb-8">
      {/* Sparkle marker above the bar (minimum-threshold indicator) */}
      <div
        aria-hidden="true"
        className="yst-absolute yst-top-0 yst-flex yst-h-5 yst-w-5 yst-items-center yst-justify-center"
        style={{ left: `${ENOUGH_PERCENT}%`, transform: 'translateX(-50%)' }}
      >
        <SparklesGradientIcon className="yst-h-4 yst-w-4" />
      </div>

      {/* Vertical tick connecting sparkle to bar (minimum-threshold indicator) */}
      <div
        aria-hidden="true"
        className={`yst-absolute yst-h-2 yst-w-px yst-transition-colors yst-duration-500 ${tickColorClass}`}
        style={{
          top: '20px',
          left: `${ENOUGH_PERCENT}%`,
          transform: 'translateX(-50%)',
        }}
      />

      <ProgressBar
        min={0}
        max={100}
        progress={percent}
        className="yst-h-2 yst-rounded-full yst-bg-slate-200 yst-overflow-hidden"
        progressClassName={getProgressClass(hasEnough, hasOptimal)}
      />

      {/* Current-phase label (bottom-left) */}
      {phaseLabel && (
        <div
          className={`yst-absolute yst-top-[44px] yst-left-0 yst-text-sm yst-font-medium yst-transition-colors yst-duration-500 ${phaseLabel.colorClass}`}
        >
          {phaseLabel.text}
        </div>
      )}

      {/* "Enough to generate" CTA pointer (bottom-right) — only once the minimum is met */}
      {hasEnough && (
        <div className="yst-absolute yst-top-[44px] yst-right-0 yst-flex yst-items-center yst-gap-1 yst-text-sm yst-font-medium yst-text-slate-500">
          <span className="yst-whitespace-nowrap">Enough to generate</span>
          <span aria-hidden="true" className="yst-leading-none">↓</span>
        </div>
      )}
    </div>
  );
};

export default ThresholdProgressBar;
